import { useTheme } from "@mui/material";
/**
 * Gets the contrast color for the text foreground based on the luminance of the background color passed in.
 * This function was pulled together from Stack Overflow answers on the topic of contrast colors.
 * I found that the MUI getLuminance() function was not returning consistent results; in my tests, this function does.
 * I suspect the inconsistent results are related to the alpha value of the color passed in.
 * @param color CSSProperties["color"]. This is the color for which you want to get the contrast color.
 * @returns CSSProperties["color"] A black or white value, depending on the luminance of the color passed in.
 */
export const getContrastColor = (color, theme) => {
    let r, g, b, alpha;
    // Return black if the color is undefined.
    if (!color)
        return theme.palette.common.black;
    if (color.startsWith("#")) {
        // Hexadecimal color
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
        alpha = 1; // Default alpha for hex colors
    }
    else if (color.startsWith("rgb")) {
        // RGB or RGBA color
        const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(\.\d+)?))?\)/);
        if (rgbMatch) {
            r = parseInt(rgbMatch[1], 10);
            g = parseInt(rgbMatch[2], 10);
            b = parseInt(rgbMatch[3], 10);
            alpha = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;
        }
        else {
            // Return black if the color is undefined.
            console.debug("Invalid RGB or RGBA color format");
            return theme.palette.common.black;
        }
    }
    else {
        // Return black if the color is unsupported.
        console.debug("Unsupported color format");
        return theme.palette.common.black;
    }
    // Check if the input color is white and return black as the contrast color.
    if (r === 255 && g === 255 && b === 255) {
        return theme.palette.common.black;
    }
    // Take alpha into account in luminance calculation.
    const luminance = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) * alpha + (1 - alpha);
    // Choose white or black based on luminance.
    const contrastColor = luminance > 0.5 ? theme.palette.common.black : theme.palette.common.white;
    // Always return contrast color with alpha 1.
    return contrastColor;
};
/**
 * A small hook to get the contrast color for a given color.
 * We use the hook instead of just importing the function so that we can leverage use of the theme.
 * @param color CSSProperties["color"]. This is the color for which you want to get the contrast color.
 * @returns CSSProperties["color"] A black or white value, depending on the luminance of the color passed in.
 */
export const useGetContrastColor = (color) => {
    const theme = useTheme();
    return getContrastColor(color, theme);
};
